import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { LoginCredentials } from '../services/authService';
import { authService } from '../services/authService';

interface LoginProps {
  onToggleMode: () => void;
  onForgotPassword: () => void;
}

const Login: React.FC<LoginProps> = ({ onToggleMode, onForgotPassword }) => {
  const [credentials, setCredentials] = useState<LoginCredentials>({
    email: '',
    password: '',
  });
  const [rememberMe, setRememberMe] = useState<boolean>(false);
  const [error, setError] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(false);
  const [showPassword, setShowPassword] = useState<boolean>(false);
  const [emailError, setEmailError] = useState<string>('');
  const [passwordError, setPasswordError] = useState<string>('');

  const { login } = useAuth();

  // Component mount olduğunda hatırlanan email'i yükle
  useEffect(() => {
    const rememberedCredentials = authService.getRememberedCredentials();
    if (rememberedCredentials) {
      setCredentials(prev => ({
        ...prev,
        email: rememberedCredentials.email,
      }));
      setRememberMe(true);
    }
  }, []);

  const validateEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!email) {
      setEmailError('E-posta alanı zorunludur');
      return false;
    }
    if (!emailRegex.test(email)) {
      setEmailError('Geçerli bir e-posta adresi girin');
      return false;
    }
    setEmailError('');
    return true;
  };

  const validatePassword = (password: string): boolean => {
    if (!password) {
      setPasswordError('Şifre alanı zorunludur');
      return false;
    }
    if (password.length < 6) {
      setPasswordError('Şifre en az 6 karakter olmalıdır');
      return false;
    }
    setPasswordError('');
    return true;
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setCredentials(prev => ({
      ...prev,
      [name]: value,
    }));
    setError('');
    
    // Real-time validation
    if (name === 'email') {
      validateEmail(value);
    } else if (name === 'password') {
      validatePassword(value);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    
    // Validate all fields
    const isEmailValid = validateEmail(credentials.email);
    const isPasswordValid = validatePassword(credentials.password);
    
    if (!isEmailValid || !isPasswordValid) {
      setError('Lütfen tüm alanları doğru şekilde doldurun');
      return;
    }
    
    setLoading(true);

    try {
      await login(credentials);
      
      // Beni hatırla seçeneğine göre email'i kaydet veya temizle
      if (rememberMe) {
        authService.saveRememberedCredentials(credentials.email);
      } else {
        authService.clearRememberedCredentials();
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Giriş başarısız');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="auth-container">
      <div className="auth-form">
        <h2>📈 Finans Uygulamasına Giriş</h2>
        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label htmlFor="email">E-posta</label>
            <input
              type="email"
              id="email"
              name="email"
              value={credentials.email}
              onChange={handleChange}
              required
              placeholder="ornek@email.com"
              className={`form-input ${emailError ? 'error' : ''}`}
              autoComplete="email"
            />
            {emailError && <div className="field-error">{emailError}</div>}
          </div>
          <div className="form-group">
            <label htmlFor="password">Şifre</label>
            <div className="password-input-container">
              <input
                type={showPassword ? 'text' : 'password'}
                id="password"
                name="password"
                value={credentials.password}
                onChange={handleChange}
                required
                placeholder="Şifreniz"
                className={`form-input ${passwordError ? 'error' : ''}`}
              />
              <button
                type="button"
                className="password-toggle-btn"
                onClick={() => setShowPassword(!showPassword)}
                aria-label={showPassword ? 'Şifreyi gizle' : 'Şifreyi göster'}
              >
                {showPassword ? '👁️‍🗨️' : '👁️'}
              </button>
            </div>
            {passwordError && <div className="field-error">{passwordError}</div>}
          </div>
          
          <div className="form-group">
            <div className="remember-me-container">
              <label htmlFor="rememberMe" className="toggle-label">
                <span className="toggle-text">Oturumumu açık tut</span>
                <div className="toggle-switch">
                  <input
                    type="checkbox"
                    id="rememberMe"
                    className="toggle-input"
                    checked={rememberMe}
                    onChange={(e) => setRememberMe(e.target.checked)}
                  />
                  <span className="toggle-slider"></span>
                </div>
              </label>
              <span className="remember-me-info">🔒 Güvenli cihazlarda kullanın</span>
            </div>
          </div>
          {error && <div className="error-message">{error}</div>}
          <button type="submit" disabled={loading} className="auth-button">
            {loading ? 'Giriş Yapılıyor...' : 'Giriş Yap'}
          </button>
        </form>
        <div className="auth-footer">
          <p>
            Hesabınız yok mu?{' '}
            <button type="button" onClick={onToggleMode} className="link-button">
              Kayıt Ol
            </button>
          </p>
          <p>
            Şifrenizi mi unuttunuz?{' '}
            <button type="button" onClick={onForgotPassword} className="link-button">
              Şifremi Unuttum
            </button>
          </p>
        </div>
      </div>
    </div>
  );
};

export default Login;
